import { Link } from 'react-router';
import { Card } from '@crave/ui';

/**
 * How to eat a Neapolitan pizza. A Neapolitan bakes 60 to 90 seconds at 850 to 900°F (the owner's
 * own numbers) and comes out soft in the middle and charred at the rim, which surprises anyone expecting a crisp chain-store slice. Saying so up
 * front turns "this is undercooked" into "this is how it is meant to be", and the folding trick is
 * the difference between eating it and wearing it.
 */

const FOLD_STEPS: [string, string][] = [
  ['Fold it in half', 'Take a slice by the rim and bring the point up to meet it.'],
  ['Fold it again', 'Now it is a long quarter. Naples calls this a portafoglio — a wallet.'],
  ['Eat from the point', 'The fold carries the soft middle. Nothing slides off.'],
];

/**
 * The wallet fold, drawn rather than photographed. The slice is shown flat from above, then end-on
 * from the crust, then bent: seen end-on, a fold is unmistakable, where a narrower triangle only
 * reads as a smaller slice.
 */
function FoldDiagram() {
  const CRUST = '#C05621';
  const TOP = '#F6E4C4';
  const EDGE = '#C8973B';
  return (
    <svg
      viewBox="0 0 340 130"
      role="img"
      aria-label="A slice seen flat with a line down the middle, then end-on, then bent in half along that line"
      style={{ width: '100%', maxWidth: 400, height: 'auto' }}
    >
      {/* Flat, from above, with the line to fold along. */}
      <path
        d="M18 26 L90 26 L54 112 Z"
        fill={TOP}
        stroke={EDGE}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path d="M18 26 L90 26" stroke={CRUST} strokeWidth="8" strokeLinecap="round" />
      <path d="M54 26 L54 112" stroke={EDGE} strokeWidth="1.5" strokeDasharray="5 5" />
      <text x="54" y="126" textAnchor="middle" fontSize="11" fill="var(--ink-soft)">
        flat
      </text>

      {/* The same slice end-on, still flat. */}
      <path d="M140 68 L206 68" stroke={TOP} strokeWidth="13" strokeLinecap="round" />
      <circle cx="141" cy="68" r="7" fill={CRUST} />
      <circle cx="205" cy="68" r="7" fill={CRUST} />
      {/* The same fold line, crossing the slice where it will bend. */}
      <path d="M173 54 L173 82" stroke={EDGE} strokeWidth="1.5" strokeDasharray="4 4" />
      <text x="173" y="126" textAnchor="middle" fontSize="11" fill="var(--ink-soft)">
        end-on
      </text>

      {/* Bent along that line: the wallet. */}
      <path
        d="M258 34 Q258 96 272 96 Q286 96 286 34"
        fill="none"
        stroke={TOP}
        strokeWidth="13"
        strokeLinecap="round"
      />
      <circle cx="258" cy="35" r="7" fill={CRUST} />
      <circle cx="286" cy="35" r="7" fill={CRUST} />
      <text x="272" y="126" textAnchor="middle" fontSize="11" fill="var(--ink-soft)">
        folded
      </text>

      {/* Arrows between the states. */}
      {[104, 224].map((x) => (
        <path
          key={x}
          d={`M${x} 68 l18 0 m-6 -6 l6 6 l-6 6`}
          fill="none"
          stroke="var(--ink-soft)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

function Point({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <strong style={{ fontSize: 17 }}>{title}</strong>
      <p style={{ margin: 0, color: 'var(--ink-soft)', lineHeight: 1.55 }}>{children}</p>
    </div>
  );
}

export function HowToEatPage() {
  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 620, margin: '0 auto' }}>
      <div>
        <div style={{ fontSize: 13, letterSpacing: '.12em', color: 'var(--ink-soft)' }}>
          NEAPOLITAN, THE WAY IT COMES
        </div>
        <h1 style={{ margin: '4px 0 0', fontSize: 30, lineHeight: 1.15 }}>
          How to eat a Neapolitan pizza
        </h1>
        <p style={{ margin: '10px 0 0', color: 'var(--ink-soft)', lineHeight: 1.55 }}>
          It is softer in the middle and darker at the rim than the pizza most of us grew up on.
          That is not a mistake — it is what 60 to 90 seconds at 850 to 900°F does. Here is how to
          get the best of it.
        </p>
      </div>

      <Card>
        <div style={{ display: 'grid', gap: 14 }}>
          <strong style={{ fontSize: 19 }}>Fold it. That is the trick.</strong>
          <FoldDiagram />
          <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 10 }}>
            {FOLD_STEPS.map(([title, detail]) => (
              <li key={title}>
                <strong>{title}.</strong> <span style={{ color: 'var(--ink-soft)' }}>{detail}</span>
              </li>
            ))}
          </ol>
          <p style={{ margin: 0, color: 'var(--ink-soft)', lineHeight: 1.55 }}>
            A knife and fork is not cheating either — that is how it is usually eaten at the table
            in Naples.
          </p>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'grid', gap: 16 }}>
          <Point title="Eat it in the first few minutes">
            This is the one that matters. A Neapolitan is at its best straight out of the oven and
            changes quickly as it cools. If you can eat it before you drive home, do.
          </Point>
          <Point title="The middle is meant to be soft">
            The centre stays wet and floppy under the tomato and mozzarella. It is not undercooked.
            The structure is in the rim, which is why you pick a slice up by the rim.
          </Point>
          <Point title="The dark spots are flavour">
            Those leopard spots on the rim come from the oven floor and the flame. Char on a
            Neapolitan is the point, not a burn.
          </Point>
          <Point title="Crack the box on the way home">
            A closed box steams the base soft. Open the lid a finger's width and the crust keeps
            more of itself.
          </Point>
          <Point title="Leftovers: a dry pan, not the microwave">
            Two or three minutes in a hot dry skillet, lid on, brings the base back. A microwave
            makes it chewy.
          </Point>
        </div>
      </Card>

      <p style={{ margin: 0, textAlign: 'center' }}>
        <Link to="/" style={{ fontWeight: 600, color: 'var(--crave-red)' }}>
          See the menu
        </Link>
      </p>
    </div>
  );
}
